import { useEffect, useRef } from 'react'

type OutsideclickProps = {
  containerRef: React.MutableRefObject<
    HTMLInputElement | HTMLDivElement | HTMLButtonElement | undefined
  >
  callback: (eventTarget?: HTMLElement) => void
}

const useOutsideOnClick = ({
  containerRef,
  callback,
}: OutsideclickProps): void => {
  /**
   * Keep the latest callback in a ref so we can register a single, stable
   * event listener for the lifetime of the component. Without this, every
   * time the parent re-renders (and produces a new inline `callback`
   * reference), the previous listener would be torn down and a new one
   * registered — creating a brief window where no listener exists and
   * causing the two EventTimeInput siblings to interfere with each other's
   * listeners during rapid parent re-renders.
   */
  const callbackRef = useRef(callback)

  useEffect(() => {
    callbackRef.current = callback
  }, [callback])

  useEffect(() => {
    const handleOutsideClick = (event: MouseEvent | TouchEvent) => {
      if (
        !containerRef.current ||
        containerRef.current.contains(event.target as Node)
      ) {
        return
      }

      callbackRef.current(event.target as HTMLElement)
    }

    document.addEventListener('mousedown', handleOutsideClick)
    document.addEventListener('touchstart', handleOutsideClick)

    return () => {
      document.removeEventListener('mousedown', handleOutsideClick)
      document.removeEventListener('touchstart', handleOutsideClick)
    }
  }, [containerRef])
}

export default useOutsideOnClick
